import { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MantineProvider, createTheme, Center, Loader } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AccessProvider, useAccess } from './access/AccessContext';
import { EditModeProvider } from './contexts/EditModeContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { MobileLayout } from './layouts/MobileLayout';
import { useGroupEventsSync } from './hooks/useGroupEvents';
import { useMQTTConnection, useGlobalMQTTSync } from './mqtt';
import { useAuthStore } from './store/authSlice';

// Plugin System
import { registerPlugins, PluginRegistry } from './plugins';

// Register all plugins on app load
registerPlugins();

// Pages
import { LoginPage } from './pages/LoginPage';
import { ProfilePage } from './pages/ProfilePage';
import { DashboardPage } from './pages/DashboardPage';
import { MorePage } from './pages/MorePage';
import { SettingsPage } from './pages/SettingsPage';
import { AdminUsersPage } from './pages/admin/UsersPage';
import { UserDetailsPage } from './pages/admin/UserDetailsPage';
import { AdminGroupsPage } from './pages/admin/GroupsPage';
import { GroupPermissionsPage } from './pages/admin/GroupPermissionsPage';
import { ProfileTypesPage } from './pages/admin/ProfileTypesPage';
import { ProfileUsersPage } from './pages/admin/ProfileUsersPage';
import { AboutPage } from './pages/AboutPage';
import { MonitoringPage } from './pages/MonitoringPage';

// Realtor Plugin MQTT Provider
import { RealtorMQTTProvider } from './plugins/realtor';

// Mantine theme with responsive settings
const theme = createTheme({
  primaryColor: 'blue',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontSizes: {
    xs: 'var(--font-size-xs)',
    sm: 'var(--font-size-sm)',
    md: 'var(--font-size-md)',
    lg: 'var(--font-size-lg)',
    xl: 'var(--font-size-xl)',
  },
  spacing: {
    xs: 'var(--spacing-xs)',
    sm: 'var(--spacing-sm)',
    md: 'var(--spacing-md)',
    lg: 'var(--spacing-lg)',
    xl: 'var(--spacing-xl)',
  },
  components: {
    Text: {
      defaultProps: {
        style: { minWidth: 0 },
      },
    },
    Badge: {
      styles: {
        root: {
          fontSize: 'var(--badge-font-size)',
          paddingTop: 'var(--badge-padding-y)',
          paddingBottom: 'var(--badge-padding-y)',
          paddingLeft: 'var(--badge-padding-x)',
          paddingRight: 'var(--badge-padding-x)',
          height: 'auto',
          minHeight: 'unset',
        },
      },
    },
    Paper: {
      styles: {
        root: {
          padding: 'var(--card-padding)',
        },
      },
    },
  },
});

// React Query client - no client-side caching, always fetch from server
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

// Placeholder pages
const AccessDeniedPage = () => <div className="p-4 text-center">Access Denied</div>;

// Permission-based route wrapper
function RequirePermission({ children, requireAdmin }: { 
  children: React.ReactNode; 
  requireAdmin?: boolean;
}) {
  const { isAdmin } = useAccess();
  
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/access-denied" replace />;
  }
  return <>{children}</>;
}

// Generate plugin routes dynamically
function PluginRoutes() {
  const routes = PluginRegistry.getRoutes();
  
  return routes.map((route) => {
    const RouteComponent = route.component;
    const element = (
      <Suspense fallback={<Center h="100%"><Loader size="sm" /></Center>}>
        <RouteComponent />
      </Suspense>
    );
    
    if (route.requireAdmin) {
      return (
        <Route 
          key={route.fullPath} 
          path={route.fullPath.slice(1)} 
          element={<RequirePermission requireAdmin>{element}</RequirePermission>} 
        />
      );
    }
    
    return <Route key={route.fullPath} path={route.fullPath.slice(1)} element={element} />;
  });
}


function AppRoutes() {
  const { isAuthenticated, isInitializing, initAuth } = useAuthStore();

  // Initialize auth on mount
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  // Initialize MQTT connection and global sync
  useMQTTConnection();
  useGlobalMQTTSync();
  useGroupEventsSync();

  // Show loader while initializing
  if (isInitializing) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  // Not authenticated - show login
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Login redirect for authenticated users */}
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/access-denied" element={<AccessDeniedPage />} />

      {/* All authenticated routes use MobileLayout with bottom navigation */}
      <Route path="/" element={
        <ProtectedRoute>
          <MobileLayout />
        </ProtectedRoute>
      }>
        {/* Dashboard - main page */}
        <Route index element={<DashboardPage />} />
        
        {/* Profile */}
        <Route path="profile" element={<ProfilePage />} />
        
        {/* More menu pages */}
        <Route path="more" element={<MorePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="about" element={<AboutPage />} />

        {/* Admin routes - require admin permission */}
        <Route path="users" element={
          <RequirePermission requireAdmin>
            <AdminUsersPage />
          </RequirePermission>
        } />
        <Route path="users/:id" element={
          <RequirePermission requireAdmin>
            <UserDetailsPage />
          </RequirePermission>
        } />
        <Route path="groups" element={
          <RequirePermission requireAdmin>
            <AdminGroupsPage />
          </RequirePermission>
        } />
        <Route path="groups/:id/permissions" element={
          <RequirePermission requireAdmin>
            <GroupPermissionsPage />
          </RequirePermission>
        } />
        <Route path="admin/profile-types" element={
          <RequirePermission requireAdmin>
            <ProfileTypesPage />
          </RequirePermission>
        } />
        <Route path="admin/profiles/:profileType/users" element={
          <RequirePermission requireAdmin>
            <ProfileUsersPage />
          </RequirePermission>
        } />
        <Route path="admin/monitoring" element={
          <RequirePermission requireAdmin>
            <MonitoringPage />
          </RequirePermission>
        } />

        {/* Plugin Routes - dynamically generated */}
        {PluginRoutes()}
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={theme} defaultColorScheme="auto">
        <Notifications position="top-center" />
        <BrowserRouter>
          <NavigationProvider>
            <AccessProvider>
              <EditModeProvider>
                <RealtorMQTTProvider>
                  <AppRoutes />
                </RealtorMQTTProvider>
              </EditModeProvider>
            </AccessProvider>
          </NavigationProvider>
        </BrowserRouter>
      </MantineProvider>
    </QueryClientProvider>
  );
}

export default App;
